import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NgControl, ValidatorFn } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

@Component({
  selector: 'gt-enhanced-autocomplete',
  templateUrl: './enhanced-autocomplete.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnhancedAutocompleteComponent<T extends { [key: string]: any }> implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() label = '';
  @Input() labelField = 'name';
  @Input() idField = 'id';
  @Input() searchFn: (name?: string) => Observable<T[]> = () => of([]);
  @Input() labelFn: (data: T) => string = (data) => data[this.labelField];
  @Input() valueMode: 'object' | 'id' = 'id';
  @Input() displayedValue?: string | null = '';

  @Output() changed = new EventEmitter<T | number | string | null>();

  private onChange: any;

  selectedValue: T | number | string | null = null;

  private displayedValue$$ = new BehaviorSubject<string | null | undefined>(this.displayedValue);
  displayedValue$ = this.displayedValue$$.pipe(map(v => v === null ? undefined : v));

  search$$ = new Subject<string>();

  private options$$ = new BehaviorSubject<{ label: string; value: any }[]>([]);
  options$ = this.options$$.asObservable();

  disabled = false;

  control: NgControl | null = null;

  autoCompleteDisplayWithFn: (value: any) => string = (obj?: T) => (obj ? (obj[this.labelField] as string) : '');

  private subs = new Subscription();

  constructor(@Optional() @Self() public controlDir: NgControl) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.controlDir) {
      const control = this.controlDir.control;
      const validators: ValidatorFn[] = control?.validator ? [control.validator] : [];
      control?.setValidators(validators);
      control?.updateValueAndValidity({ emitEvent: false });
    }
    this.subs.add(
      this.search$$.pipe(debounceTime(300)).subscribe(value => this.search(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.displayedValue) {
      this.displayedValue$$.next(changes.displayedValue.currentValue);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  writeValue(val: any) {
    this.selectedValue = val;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  search(value?: string) {
    if (this.searchFn) {
      this.searchFn(value).subscribe((results) => {
        this.options$$.next(
          results.map((r) => ({ label: this.labelFn(r), value: this.valueMode === 'object' ? r : r[this.idField] }))
        );
      });
    }
  }

  select(evt: MatAutocompleteSelectedEvent) {
    const oldValue = this.selectedValue;
    if (evt) {
      const { option } = evt;
      this.selectedValue = option.value;
      this.displayedValue$$.next(option.getLabel());
    } else {
      this.selectedValue = null;
      this.displayedValue$$.next('');
    }
    if (this.selectedValue !== oldValue) {
      this.onChange(this.selectedValue);
      this.changed.emit(this.selectedValue);
    }
  }

  clear() {
    if (this.selectedValue !== null) {
      this.selectedValue = null;
    }
    this.displayedValue$$.next('');
    this.options$$.next([]);
    this.onChange(this.selectedValue);
    this.changed.emit(this.selectedValue);
  }

  onTouched() { }
}
